import { useState, useEffect } from 'react';

// Session storage key for hiding the banner
const HIDE_BANNER_KEY = 'hideUpdateBanner';

export default function useUpdateNotification({ isVisible, onRefresh, onDismiss, onLater, autoRefresh = false, countdown = 0 }) {
  const [timeLeft, setTimeLeft] = useState(countdown);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Check sessionStorage on mount to see if banner was dismissed this session
  useEffect(() => {
    const isHidden = sessionStorage.getItem(HIDE_BANNER_KEY) === 'true';
    if (isVisible && !isHidden) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (!isVisible) {
      // Start close animation
      if (shouldRender) {
        setIsClosing(true);
        const timer = setTimeout(() => {
          setShouldRender(false);
        }, 200); // Match animation duration
        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, shouldRender]);

  // Auto-refresh countdown
  useEffect(() => {
    if (autoRefresh && countdown > 0) {
      setTimeLeft(countdown);
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            onRefresh();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [autoRefresh, countdown, onRefresh]);

  // Handle dismiss with sessionStorage
  const handleDismissClick = () => {
    sessionStorage.setItem(HIDE_BANNER_KEY, 'true');
    setIsClosing(true);
    setTimeout(() => {
      onDismiss();
    }, 200);
  };

  // Handle later with sessionStorage
  const handleLaterClick = () => {
    sessionStorage.setItem(HIDE_BANNER_KEY, 'true');
    setIsClosing(true);
    setTimeout(() => {
      onLater();
    }, 200);
  };

  // Check if hidden in sessionStorage
  const isHiddenInSession = sessionStorage.getItem(HIDE_BANNER_KEY) === 'true';

  return {
    timeLeft,
    isClosing,
    shouldRender,
    handleDismissClick,
    handleLaterClick,
    isHiddenInSession
  };
}
